import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ContactForm from '../components/ContactForm'
import PriceCalculator from '../components/PriceCalculator'
import { updatePageSEO } from '../lib/seo'

export default function ServiceDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const contactFormRef = useRef(null)

  const otherServices = [
    { title: 'Renovering af hus', slug: 'renovering-af-hus' },
    { title: 'Nyt tag', slug: 'nyt-tag' },
    { title: 'Udskiftning af vinduer', slug: 'udskiftning-af-vinduer' },
    { title: 'Opbygning af træterrasse', slug: 'opbygning-af-traeterrasse' },
    { title: 'Totalentreprise', slug: 'totalentreprise' },
    { title: 'Tømreropgaver', slug: 'toemreropgaver' },
  ].filter(s => s.slug !== slug)

  useEffect(() => {
    window.scrollTo(0, 0)
    setLoading(true)

    const fetchPage = async () => {
      try {
        // Fetch using the v2 endpoint with filter on url_slug
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/pages?url_slug=${slug}`)
        if (res.ok) {
          const data = await res.json()
          if (data.items && data.items.length > 0) {
            const pageItem = data.items[0]
            setPage(pageItem)
            
            // Set SEO metadata
            updatePageSEO(
              pageItem.meta_title || `${pageItem.title} - BAW Byg og Konstruktion`,
              pageItem.meta_description
            )
          } else {
            // Page not found, redirect to 404
            navigate('/404', { replace: true })
          }
        } else {
          navigate('/404', { replace: true })
        }
      } catch (err) {
        console.error('Error fetching service detail:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPage()
  }, [slug, navigate])

  const handleScrollToContact = () => {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Henter indhold...</p>
        </div>
      </div>
    )
  }

  if (!page) return null

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="bg-[#111111] text-white py-20 relative overflow-hidden border-b border-[#222222]">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=50,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/4-53.jpg"
            alt={page.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 text-left space-y-6">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-orange block">YDELSER &amp; KOMPETENCER</span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight uppercase">{page.title}</h1>
          <div>
            <button
              onClick={handleScrollToContact}
              className="border border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white font-bold py-3.5 px-8 rounded-none transition-all duration-300 text-xs flex items-center gap-2 cursor-pointer uppercase tracking-widest"
            >
              Få et uforpligtende tilbud
              <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-24 mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 bg-white">
        {/* Left column: Content */}
        <div className="lg:col-span-7 space-y-12">
          {/* Render page content HTML */}
          <div
            className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:uppercase prose-headings:tracking-wide prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600 prose-strong:text-[#111111] prose-strong:font-bold prose-a:text-brand-orange prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />

          {/* Special element: Price Calculator for Wooden Terrace */}
          {slug === 'opbygning-af-traeterrasse' && (
            <div className="pt-12 border-t border-slate-100">
              <PriceCalculator onContactClick={handleScrollToContact} />
            </div>
          )}

          {/* Page Images */}
          {page.images && page.images.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-12 border-t border-slate-100">
              {page.images.map((img, idx) => (
                <div key={idx} className="overflow-hidden rounded-none border border-slate-200 shadow-none">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${page.title} - Billede ${idx + 1}`}
                    className="w-full h-64 object-cover hover:scale-102 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column: Sidebar */}
        <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24 lg:self-start">
          {/* Contact Form Widget */}
          <div ref={contactFormRef} className="bg-slate-50 p-6 md:p-8 rounded-none border border-slate-200 space-y-6">
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-orange block">FÅ ET TILBUD</span>
              <h3 className="font-display font-bold text-lg text-[#111111] uppercase tracking-wider">
                Skriv til os i dag
              </h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Udfyld formularen herunder, så vender vi tilbage til dig hurtigst muligt med et uforpligtende tilbud.
              </p>
            </div>
            <ContactForm subject={`Henvendelse vedr. ${page.title}`} dark={false} />
          </div>

          {/* Quick Contact Box */}
          <div className="bg-[#222222] text-white p-8 rounded-none border border-[#333333] space-y-6">
            <h3 className="font-display font-bold text-lg text-brand-orange uppercase tracking-wider">
              Hurtig kontakt
            </h3>
            <p className="text-slate-300 text-xs leading-relaxed">
              Vi står altid klar til at rådgive dig og give et uforpligtende tilbud på dit projekt. Ring eller skriv til os i dag!
            </p>
            <div className="space-y-4 pt-2 text-sm">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-brand-orange"></i>
                <a href="tel:+4529678107" className="hover:text-brand-orange transition-colors font-bold whitespace-nowrap">
                  +45 2967 8107
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-brand-orange"></i>
                <a href="mailto:kontakt@baw-byg.dk" className="hover:text-brand-orange transition-colors font-bold whitespace-nowrap">
                  kontakt@baw-byg.dk
                </a>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-brand-orange mt-0.5"></i>
                <span className="text-slate-300">
                  Øster Kringelvej 7, 8250 Egå
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
